// One timeline look for every kitchen-flow document: requests (Lane C),
// production plans and purchase orders (Stage 3). Oldest first; who did it,
// and who authorised it with a PIN.
import { useQuery } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { formatDateHuman } from '@/lib/utils'
import { TL, timelineLabel, timelineWhat } from '@/lib/inventoryFlow/timelineStrings'

export interface TimelineItem {
  key: string
  at: string
  label: string
  what?: string
  ref?: string
  actorName?: string | null
  authorizedByName?: string | null
  note?: string | null
}

export function whenLabel(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return `${formatDateHuman(d)} ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
}

export function DocumentTimeline({ title, items, by, pinBy }: { title: string; items: TimelineItem[]; by: string; pinBy: string }) {
  if (items.length === 0) return null
  return (
    <section className="space-y-2">
      <h3 className="font-semibold">{title}</h3>
      <ol className="space-y-2 border-l-2 pl-4">
        {items.map((e) => (
          <li key={e.key} className="text-sm">
            <div>
              <span className="font-medium">{e.label}</span>
              {e.what && <span> — {e.what}</span>}
              {e.ref && <span className="text-muted-foreground"> · {e.ref}</span>}
              {e.actorName && <span className="text-muted-foreground"> · {by} {e.actorName}</span>}
              {e.authorizedByName && <span className="text-muted-foreground"> · {pinBy}: {e.authorizedByName}</span>}
            </div>
            <div className="text-xs text-muted-foreground">
              {whenLabel(e.at)}
              {e.note && ` · ${e.note}`}
            </div>
          </li>
        ))}
      </ol>
    </section>
  )
}

/** Fetched timeline for a production plan or a purchase order (flag on only). */
export function FetchedTimeline({ kind, id }: { kind: 'plan' | 'po'; id: string }) {
  const q = useQuery({
    queryKey: ['inventory-flow', 'timeline', kind, id],
    queryFn: () => (kind === 'plan' ? apiClient.getProductionPlanTimeline(id) : apiClient.getPurchaseOrderTimeline(id)),
  })
  if (q.isError) return <p className="text-sm text-muted-foreground">{TL.failed}</p>
  const events = q.data?.data?.events ?? []
  const items: TimelineItem[] = events.map((e, i) => ({
    key: `${i}-${e.at}-${e.event_type}`,
    at: e.at,
    label: timelineLabel(e),
    what: timelineWhat(e) || e.summary,
    ref: e.ref,
    actorName: e.actor_name,
    authorizedByName: e.authorized_by_name,
    note: e.note,
  }))
  return <DocumentTimeline title={TL.title} items={items} by={TL.by} pinBy={TL.pin} />
}
